import { Copy, TextSelect, Reply, Share2, Megaphone, Camera, Trash2, X } from 'lucide-react'
import { Dialog as HeadlessDialog, DialogBackdrop, DialogPanel } from '@headlessui/react'
import type { ChatMessage } from '@/lib/sharedTypes'
import { useDragDismiss } from '@/hooks/useDragDismiss'

interface ChatMessageActionSheetProps {
  message: ChatMessage | null
  isOwn: boolean
  onClose: () => void
  onReply: () => void
  onCopy: () => void
  onSelectCopy: () => void
  onShare: () => void
  onAnnounce: () => void
  onCapture: () => void
  onDelete: () => void
  onReaction?: (emoji: string) => void
}

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🔥']

export function ChatMessageActionSheet({
  message,
  isOwn,
  onClose,
  onReply,
  onCopy,
  onSelectCopy,
  onShare,
  onAnnounce,
  onCapture,
  onDelete,
  onReaction,
}: ChatMessageActionSheetProps) {
  // Headless UI Dialog: 포커스 트랩 / Escape 닫기 / 스크롤 잠금 / aria-modal 제공
  const open = message != null
  const { sheetRef, dragHandlers } = useDragDismiss({ onClose })

  const actions = message
    ? [
        ...(message.text ? [{ icon: Copy, label: '복사', onClick: onCopy }] : []),
        ...(message.text ? [{ icon: TextSelect, label: '선택 복사', onClick: onSelectCopy }] : []),
        { icon: Reply, label: '답장', onClick: onReply },
        { icon: Share2, label: '공유', onClick: onShare },
        ...(message.text ? [{ icon: Megaphone, label: '공지', onClick: onAnnounce }] : []),
        { icon: Camera, label: '캡처', onClick: onCapture },
        ...(isOwn && !message.isDeleted ? [{ icon: Trash2, label: '삭제', onClick: onDelete, danger: true }] : []),
      ]
    : []

  return (
    <HeadlessDialog open={open} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/30 transition-opacity duration-200 data-[closed]:opacity-0"
      />

      {/* Sheet */}
      <div className="fixed inset-x-0 bottom-0 z-50">
        <DialogPanel
          ref={sheetRef}
          transition
          className="bg-surface-primary rounded-t-2xl shadow-2xl pb-safe transition-transform duration-200 data-[closed]:translate-y-full"
        >
          {/* Handle — drag-to-dismiss */}
          <div className="flex justify-center pt-3 pb-2" {...dragHandlers}>
            <div className="w-10 h-1 rounded-full bg-surface-tertiary" />
          </div>

          {/* 빠른 리액션 */}
          {onReaction && (
            <div className="flex items-center justify-between gap-1 px-4 py-2 border-b border-base">
              {QUICK_REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => { onReaction(emoji); onClose() }}
                  className="text-2xl min-w-[44px] min-h-[44px] flex items-center justify-center hover:scale-125 active:scale-95 transition-transform"
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}

          {/* Preview — drag area */}
          <div className="px-4 py-2.5 border-b border-base" {...dragHandlers}>
            <p className="text-xs text-caption truncate">
              {message?.userName}: {message?.text || '(미디어 메시지)'}
            </p>
          </div>

          {/* Actions */}
          <div className="py-2 max-h-[50vh] overflow-y-auto">
            {actions.map((action) => {
              const Icon = action.icon
              const isDanger = 'danger' in action && action.danger
              return (
                <button
                  key={action.label}
                  onClick={() => { action.onClick(); onClose() }}
                  className={`w-full flex items-center gap-3 px-5 py-3.5 text-sm transition-colors ${
                    isDanger
                      ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10'
                      : 'text-heading hover-surface'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{action.label}</span>
                </button>
              )
            })}
          </div>

          {/* Cancel */}
          <div className="border-t border-base py-2">
            <button
              onClick={onClose}
              className="w-full flex items-center gap-3 px-5 py-3.5 text-sm text-caption hover-surface"
            >
              <X className="w-5 h-5" />
              <span>닫기</span>
            </button>
          </div>
        </DialogPanel>
      </div>
    </HeadlessDialog>
  )
}
